const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const formatLabel = (value, fallback = '') => {
  const label = String(value || '').trim().replace(/_/g, ' ');
  if (!label) return fallback;
  return label.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTransactionNumber = (row) => {
  const number = Number(row?.sale_trans_number);
  if (Number.isInteger(number) && number > 0) {
    return `#${String(number).padStart(4, '0')}`;
  }
  const id = String(row?.sales_trans_id || row?.id || '').trim();
  return id ? `#${id.slice(0, 8)}` : '';
};

export const slugifyForExport = (value) => String(value || 'sale')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80) || 'sale';

const csvQuote = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const makeCsv = (headers, rows) => {
  const lines = [
    headers.map((header) => csvQuote(header.label)).join(','),
    ...rows.map((row) => headers.map((header) => csvQuote(header.value(row))).join(',')),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
};

const getDosDateTime = (date = new Date()) => {
  const year = Math.max(date.getFullYear(), 1980);
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosDate, dosTime };
};

const makeCrcTable = () => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
};

const crcTable = makeCrcTable();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const writeHeader = (size) => {
  const bytes = new Uint8Array(size);
  return {
    bytes,
    view: new DataView(bytes.buffer),
  };
};

const concatBytes = (parts) => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
};

const createZipBlob = (files) => {
  const encoder = new TextEncoder();
  const now = getDosDateTime();
  const localParts = [];
  const centralParts = [];
  let localOffset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content);
    const checksum = crc32(dataBytes);

    const local = writeHeader(30);
    local.view.setUint32(0, 0x04034b50, true);
    local.view.setUint16(4, 20, true);
    local.view.setUint16(6, 0x0800, true);
    local.view.setUint16(8, 0, true);
    local.view.setUint16(10, now.dosTime, true);
    local.view.setUint16(12, now.dosDate, true);
    local.view.setUint32(14, checksum, true);
    local.view.setUint32(18, dataBytes.length, true);
    local.view.setUint32(22, dataBytes.length, true);
    local.view.setUint16(26, nameBytes.length, true);
    local.view.setUint16(28, 0, true);

    localParts.push(local.bytes, nameBytes, dataBytes);

    const central = writeHeader(46);
    central.view.setUint32(0, 0x02014b50, true);
    central.view.setUint16(4, 20, true);
    central.view.setUint16(6, 20, true);
    central.view.setUint16(8, 0x0800, true);
    central.view.setUint16(10, 0, true);
    central.view.setUint16(12, now.dosTime, true);
    central.view.setUint16(14, now.dosDate, true);
    central.view.setUint32(16, checksum, true);
    central.view.setUint32(20, dataBytes.length, true);
    central.view.setUint32(24, dataBytes.length, true);
    central.view.setUint16(28, nameBytes.length, true);
    central.view.setUint16(30, 0, true);
    central.view.setUint16(32, 0, true);
    central.view.setUint16(34, 0, true);
    central.view.setUint16(36, 0, true);
    central.view.setUint32(38, 0, true);
    central.view.setUint32(42, localOffset, true);
    centralParts.push(central.bytes, nameBytes);

    localOffset += local.bytes.length + nameBytes.length + dataBytes.length;
  }

  const centralOffset = localOffset;
  const centralBytes = concatBytes(centralParts);
  const end = writeHeader(22);
  end.view.setUint32(0, 0x06054b50, true);
  end.view.setUint16(4, 0, true);
  end.view.setUint16(6, 0, true);
  end.view.setUint16(8, files.length, true);
  end.view.setUint16(10, files.length, true);
  end.view.setUint32(12, centralBytes.length, true);
  end.view.setUint32(16, centralOffset, true);
  end.view.setUint16(20, 0, true);

  return new Blob([concatBytes([...localParts, centralBytes, end.bytes])], { type: 'application/zip' });
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const rpcJson = async (fetchWrapper, apiUrl, endpoint, body) => {
  const response = await fetchWrapper(`${apiUrl}/supabase/rpc/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response?.json?.().catch(() => []);
  if (!response?.ok) {
    throw new Error(data?.error?.message || `Failed to load ${endpoint}.`);
  }
  return Array.isArray(data) ? data : [];
};

const buildCategoryRows = (itemRows) => {
  const grouped = new Map();
  for (const row of itemRows) {
    const category = String(row?.category || '').trim() || 'Uncategorized';
    const quantity = Math.max(1, Math.floor(toNum(row?.quantity, 1)));
    const soldUnitPrice = toNum(row?.sold_unit_price, 0);
    const askingUnitPrice = toNum(row?.asking_unit_price, 0);
    const lineSubtotal = toNum(row?.line_subtotal, soldUnitPrice * quantity);
    const isManual = Boolean(row?.is_manual);
    const existing = grouped.get(category) || {
      category,
      line_count: 0,
      qty_total: 0,
      sold_total: 0,
      asking_total: 0,
      manual_line_count: 0,
    };
    existing.line_count += 1;
    existing.qty_total += quantity;
    existing.sold_total += lineSubtotal;
    existing.asking_total += askingUnitPrice * quantity;
    if (isManual) existing.manual_line_count += 1;
    grouped.set(category, existing);
  }
  return [...grouped.values()]
    .map((row) => ({
      ...row,
      avg_sold_unit_price: row.qty_total > 0 ? row.sold_total / row.qty_total : 0,
    }))
    .sort((a, b) => toNum(b.sold_total, 0) - toNum(a.sold_total, 0));
};

export const downloadSaleExportPackage = async ({
  apiUrl,
  clientId,
  fetchWrapper,
  sale,
}) => {
  const saleId = sale?.id;
  if (!apiUrl || !clientId || !fetchWrapper || !saleId) {
    throw new Error('Sale export is missing required context.');
  }

  const saleName = String(sale?.sale_name || sale?.name || 'sale').trim() || 'sale';
  const saleSlug = slugifyForExport(saleName);
  const payload = {
    p_client_id: clientId,
    p_sale_id: saleId,
  };

  const [transactions, transactionItems, inventoryRows] = await Promise.all([
    rpcJson(fetchWrapper, apiUrl, 'select_sales_trans', payload),
    rpcJson(fetchWrapper, apiUrl, 'select_sales_trans_items', payload),
    rpcJson(fetchWrapper, apiUrl, 'select_inventory_items_by_sale_id', {
      p_client_id: clientId,
      p_sale_id: saleId,
      p_section_id: null,
    }),
  ]);

  const categoryRows = buildCategoryRows(transactionItems);
  const zipFiles = [
    {
      name: `${saleSlug}-sales-transactions.csv`,
      content: makeCsv([
        { label: 'Transaction Date', value: (row) => formatDateTime(row.created_at) },
        { label: 'Sale Number', value: (row) => formatTransactionNumber(row) },
        { label: 'Sale Name', value: (row) => row.sale_name || saleName },
        { label: 'Payment Method', value: (row) => formatLabel(row.payment_method) },
        { label: 'Tax Exempt', value: (row) => (row.is_tax_exempt ? 'Yes' : 'No') },
        { label: 'Tax Exempt Business', value: (row) => row.tax_exempt_business_name || '' },
        { label: 'Tax Exempt ID', value: (row) => row.tax_exempt_tax_id || '' },
        { label: 'Items', value: (row) => row.item_count || 0 },
        { label: 'Subtotal', value: (row) => toNum(row.subtotal_amount, 0).toFixed(2) },
        { label: 'Tax', value: (row) => toNum(row.tax_amount, 0).toFixed(2) },
        { label: 'Total', value: (row) => toNum(row.total_amount, 0).toFixed(2) },
        { label: 'Status', value: (row) => formatLabel(row.status) },
        { label: 'Transaction ID', value: (row) => row.id || '' },
      ], transactions),
    },
    {
      name: `${saleSlug}-transaction-detail.csv`,
      content: makeCsv([
        { label: 'Transaction Date', value: (row) => formatDateTime(row.trans_created_at || row.created_at) },
        { label: 'Sale Number', value: (row) => formatTransactionNumber(row) },
        { label: 'Sale Name', value: (row) => row.sale_name || saleName },
        { label: 'Title', value: (row) => row.title || '' },
        { label: 'Category', value: (row) => row.category || '' },
        { label: 'Quantity', value: (row) => row.quantity || 0 },
        { label: 'Asking Unit', value: (row) => toNum(row.asking_unit_price, 0).toFixed(2) },
        { label: 'Sold Unit', value: (row) => toNum(row.sold_unit_price, 0).toFixed(2) },
        { label: 'Line Total', value: (row) => toNum(row.line_subtotal, 0).toFixed(2) },
        { label: 'Manual', value: (row) => (row.is_manual ? 'Yes' : 'No') },
        { label: 'Payment Method', value: (row) => formatLabel(row.payment_method) },
        { label: 'Status', value: (row) => formatLabel(row.status) },
        { label: 'Tax Exempt', value: (row) => (row.is_tax_exempt ? 'Yes' : 'No') },
        { label: 'Transaction ID', value: (row) => row.sales_trans_id || '' },
        { label: 'Line ID', value: (row) => row.id || '' },
      ], transactionItems),
    },
    {
      name: `${saleSlug}-category-summary.csv`,
      content: makeCsv([
        { label: 'Category', value: (row) => row.category },
        { label: 'Lines', value: (row) => row.line_count },
        { label: 'Quantity', value: (row) => row.qty_total },
        { label: 'Asking Total', value: (row) => toNum(row.asking_total, 0).toFixed(2) },
        { label: 'Sold Total', value: (row) => toNum(row.sold_total, 0).toFixed(2) },
        { label: 'Average Sold Unit', value: (row) => toNum(row.avg_sold_unit_price, 0).toFixed(2) },
        { label: 'Manual Lines', value: (row) => row.manual_line_count },
      ], categoryRows),
    },
    {
      name: `${saleSlug}-remaining-inventory.csv`,
      content: makeCsv([
        { label: 'Filename', value: (row) => row.filename || '' },
        { label: 'Section', value: (row) => row.section_name || '' },
        { label: 'Title', value: (row) => row.title || '' },
        { label: 'Description', value: (row) => row.description || '' },
        { label: 'Category', value: (row) => row.category || '' },
        { label: 'Quantity', value: (row) => row.quantity_estimate || 0 },
        { label: 'Price', value: (row) => toNum(row.per_item_cost_estimate, 0).toFixed(2) },
        { label: 'Extended Value', value: (row) => (toNum(row.per_item_cost_estimate, 0) * Math.max(1, Math.floor(toNum(row.quantity_estimate, 1)))).toFixed(2) },
        { label: 'Resale Positioning', value: (row) => Array.isArray(row.resale_positioning) ? row.resale_positioning.join('; ') : row.resale_positioning || '' },
        { label: 'Pricing Context', value: (row) => Array.isArray(row.pricing_context) ? row.pricing_context.join('; ') : row.pricing_context || '' },
        { label: 'Tagged', value: (row) => (row.tagged ? 'Yes' : 'No') },
        { label: 'Inventory Item ID', value: (row) => row.id || '' },
      ], inventoryRows),
    },
  ];

  const zipBlob = createZipBlob(zipFiles);
  const fileName = `${saleSlug}-sale-export.zip`;
  downloadBlob(zipBlob, fileName);
  return fileName;
};
